import React, { useRef, useState, useMemo, useCallback, useEffect } from 'react';
import { useFocusWithin } from '@react-aria/interactions';
import PropTypes from '@squiz/xaccel-prop-types';
import { generateClasses } from '@squiz/xaccel-utility-functions';
import AssistedInputContext from './AssistedInputContext';
import styles from '../css/global.module.scss';
import { jsx as _jsx } from "react/jsx-runtime";
import { jsxs as _jsxs } from "react/jsx-runtime";
function AssistedInputContainer(_ref) {
  let {
    id,
    onSubmit,
    alwaysShowResults,
    children,
    className,
    ...restProps
  } = _ref;
  const assistMap = useRef(new Map()); // Use a map so the insert order is guaranteed in iteration
  const inputRef = useRef();
  const [isFetching, setIsFetching] = useState(false); // Track is input focused to show or hide assisted input
  const [isInputFocused, setIsInputFocused] = useState(false); // Track is input focused to show or hide assisted input
  const [inputValue, setInputValue] = useState('');
  const [readerAnnounce, setReaderAnnounce] = useState('');
  const [activeDescendant, setActiveDescendant] = useState(null);
  const [assistedItems, setAssistedItems] = useState([]);
  const [isAssistShowable, setIsAssistShowable] = useState(alwaysShowResults); // This tracks if the component is in a state that CAN show the assist
  const [showResults, setShowResults] = useState(alwaysShowResults); // This tracks if the assist is being shown

  // Handles updating the ARIA announcer for the number of items in the assist
  useEffect(() => {
    // If input is clear, clear the reader announcement
    if (!inputValue) {
      setReaderAnnounce('');
      return;
    }

    // If multiple sources exist
    if (assistMap.current.size > 1) {
      // Calculate how many
      let sources = 0;
      assistMap.current.forEach(childIdArray => {
        if (childIdArray.length > 0) {
          sources += 1;
        }
      });

      // Set the reader announcement
      setReaderAnnounce(assistedItems.length > 0 ? `${assistedItems.length} results for ${inputValue} from ${sources} sources` : `No results found for ${inputValue} from ${assistMap.current.size} sources`);
    } else {
      // Set the reader announcement
      setReaderAnnounce(assistedItems.length > 0 ? `${assistedItems.length} results for ${inputValue}` : `No results found for ${inputValue}`);
    }
  }, [inputValue, assistedItems]);

  // Handles activating the assist items when the state is correct
  useEffect(() => {
    if (isAssistShowable && isInputFocused) {
      setShowResults(true);
    } else {
      setActiveDescendant(null);
      if (!alwaysShowResults) {
        setShowResults(false);
      }
    }
  }, [isAssistShowable, isInputFocused]);
  useEffect(() => {
    if (isAssistShowable && isInputFocused) {
      setShowResults(true);
    } else {
      setActiveDescendant(null);
      if (!alwaysShowResults) {
        setShowResults(false);
      }
    }
  }, [isAssistShowable, isInputFocused]);

  // Function allows child sources to register their item ids for the keyboard navigation to traverse
  const registerChildIds = useCallback((sourceId, childIds) => {
    assistMap.current.set(sourceId, childIds);
    const newAssistedItems = [];
    assistMap.current.forEach(childIdArray => {
      childIdArray.forEach(childId => {
        newAssistedItems.push(childId);
      });
    });
    setAssistedItems(newAssistedItems);
    // If it has items set the assist to be showable
    if (newAssistedItems.length > 0) {
      setIsAssistShowable(true);
    } else {
      // If not, its not showable and hide it
      setIsAssistShowable(false);
      setShowResults(false);
    }
  }, []);

  // Function allows the child input element to register itself without the end user needing to pass refs around
  const registerInputRef = useCallback(ref => {
    inputRef.current = ref.current;
  }, []);

  // Function handles the input text change from the child input element, via the context it passes this update to all child sources
  const handleInputChange = useCallback(_ref2 => {
    let {
      currentTarget: {
        value
      },
      closeAssist
    } = _ref2;
    setInputValue(value);
    if (closeAssist) {
      if (!alwaysShowResults) {
        setShowResults(false);
      }
    }
  }, []);
  const submitForm = () => {
    onSubmit();
  };

  // Function to handle closing the assisted input in response to user action e.g ESC key
  const handleDismissAssist = useCallback(() => {
    if (!alwaysShowResults) {
      // Hide results
      setShowResults(false);
    }

    // After closing results focus the input field
    if (inputRef.current) {
      inputRef.current.focus();
    }
  });

  // Function handles the keyboard ally controls for the whole context
  const handleKeyUp = useCallback(e => {
    let index = -1;
    const activeDescendantIndex = assistedItems.findIndex(value => value === activeDescendant);
    const totalAssistItems = assistedItems.length;
    if (e.key === 'Escape') {
      handleDismissAssist(e);
      return;
    }
    if (e.key === 'ArrowLeft' || e.key === 'ArrowRight') {
      // Don't want to re-show the assist if its been hidden and they are using arrow keys
      return;
    }
    if (e.key === 'ArrowDown') {
      index = activeDescendantIndex + 1 >= totalAssistItems ? 0 : activeDescendantIndex + 1;
    }
    if (e.key === 'ArrowUp') {
      index = activeDescendantIndex - 1 < 0 ? totalAssistItems - 1 : activeDescendantIndex - 1;
    }
    if (e.key === 'Enter') {
      /*
          Enter has to be handled in a keyDown event due to an escape/dismiss button passing focus to the input.
          If the end user hits Enter on that button, the focus changes BEFORE the keyup fires, so it fire HERE
          and would submit the form when thats not the intended action.
           As the keyDown in that circumstance has already fired, handling the submit in keyDown prevent an
          incorrect submit.
      */
      return;
    }
    if (index !== -1) {
      const nextActiveDescendant = document.querySelector(`#${assistedItems[index]}`);
      if (nextActiveDescendant) {
        nextActiveDescendant.scrollIntoView({
          behavior: 'smooth',
          block: 'nearest',
          inline: 'start'
        });
        setActiveDescendant(nextActiveDescendant.id);
      }
    } else {
      setActiveDescendant(null);
    }
    if (isAssistShowable) {
      setShowResults(true);
    }
  }, [assistedItems, activeDescendant, showResults, isAssistShowable]);
  const handleKeyDown = useCallback(e => {
    if (e.key === 'Enter') {
      // Nothing selected and enter hit we will post form.
      if (activeDescendant === null) {
        submitForm();
      } else {
        document.querySelector(`#${activeDescendant}`).click();
        setActiveDescendant(null);
        setIsInputFocused(true);
        e.preventDefault();
      }
    } else if (e.key === 'ArrowDown' || e.key === 'ArrowUp' || e.key === 'Escape') {
      // This wil prevent moving cursor on input field when press down or up also stop form from being posted when enter pressed.
      e.preventDefault();
    } else {
      // Regain focus on input field when other buttons are entered.
      inputRef.current.focus();
    }
  }, [activeDescendant]);
  const contextValue = useMemo(() => ({
    id,
    assistActive: showResults,
    isFetching,
    inputValue,
    activeDescendant,
    isPopup: !alwaysShowResults,
    // Set if input should aria-haspopup itself
    registerChildIds,
    registerInputRef,
    onInputChange: handleInputChange,
    onKeyUp: handleKeyUp,
    onKeyDown: handleKeyDown,
    setIsFetching
  }), [inputValue, assistedItems, activeDescendant, showResults, alwaysShowResults, isFetching]);
  const {
    focusWithinProps
  } = useFocusWithin({
    onFocusWithin: () => {
      setIsAssistShowable(true);
      setIsInputFocused(true);
    },
    onBlurWithin: () => {
      setIsAssistShowable(false);
      setIsInputFocused(false);
    }
  });
  return /*#__PURE__*/_jsxs("div", {
    ...focusWithinProps,
    className: generateClasses({
      block: 'assisted-input-container',
      modifiers: {
        showing: inputValue !== '' && showResults
      },
      override: className
    }, styles),
    ...restProps,
    children: [/*#__PURE__*/_jsx(AssistedInputContext.Provider, {
      value: contextValue,
      children: children
    }), /*#__PURE__*/_jsx("span", {
      "aria-live": "polite",
      id: `assisted-input-announce-${id}`,
      className: generateClasses({
        block: 'assisted-input-container',
        element: 'announce',
        override: className
      }, styles),
      children: readerAnnounce
    }), !alwaysShowResults && showResults && /*#__PURE__*/_jsx("button", {
      type: "button",
      tabIndex: -1,
      onClick: handleDismissAssist,
      className: generateClasses({
        block: 'assisted-input-container',
        element: 'dismiss-button',
        override: className
      }, styles),
      children: "Dismiss assisted input"
    })]
  });
}
AssistedInputContainer.propTypes = {
  id: PropTypes.string.isRequired,
  onSubmit: PropTypes.func.isRequired,
  alwaysShowResults: PropTypes.bool,
  children: PropTypes.node,
  className: PropTypes.classNameOverride
};
AssistedInputContainer.defaultProps = {
  alwaysShowResults: false,
  children: undefined,
  className: undefined
};
export default AssistedInputContainer;